/* p5.bezier library by Peiling Jiang */
import { _smoothness } from './coefficients';
import { _sampleBezier } from './sampling';
import { _concentrate, _copy, _getCanvasUtils, _getCloseCurvePoints, _samePoints, _validateSmoothness, } from './utils';
function _preparePoints(points, closeType) {
    if (points.length < 2) {
        throw new Error('[p5.bezier] At least 2 points are needed to draw a curve');
    }
    var prepared = _concentrate(points, closeType === 'CLOSE');
    if (closeType === 'CLOSE')
        prepared.push.apply(prepared, _getCloseCurvePoints(prepared));
    return prepared;
}
function _drawVertices(canvas, vertices, closeType) {
    canvas.beginPath();
    var first = vertices[0];
    canvas.moveTo(first[0], first[1], first[2]);
    for (var i = 1; i < vertices.length; i++) {
        var vertex = vertices[i];
        canvas.lineTo(vertex[0], vertex[1], vertex[2]);
    }
    canvas.closePath(closeType);
}
var P5Bezier = /** @class */ (function () {
    // biome-ignore lint/suspicious/noExplicitAny: p5 typing
    function P5Bezier(canvas) {
        this.points = [];
        this.vertices = [];
        this.increment = 0;
        this.b = _getCanvasUtils(canvas);
    }
    P5Bezier.prototype.draw = function (pointList, closeType, smoothness) {
        if (closeType === void 0) { closeType = 'OPEN'; }
        if (smoothness === void 0) { smoothness = 3; }
        var points = _preparePoints(pointList, closeType);
        var increment = _smoothness[_validateSmoothness(smoothness)];
        if (increment !== this.increment || !_samePoints(points, this.points)) {
            _sampleBezier(points, this.b.dimension, increment, this.vertices);
            _copy(points, this.points);
            this.increment = increment;
        }
        _drawVertices(this.b, this.vertices, closeType);
        return points;
    };
    P5Bezier.prototype.new = function (pointList, closeType, smoothness) {
        if (closeType === void 0) { closeType = 'OPEN'; }
        if (smoothness === void 0) { smoothness = 3; }
        var increment = _smoothness[_validateSmoothness(smoothness)];
        return new BezierCurve(pointList, closeType, increment, this.b);
    };
    return P5Bezier;
}());
// biome-ignore lint/suspicious/noExplicitAny: p5 typing
function initBezier(canvas) {
    return new P5Bezier(canvas);
}
var BezierCurve = /** @class */ (function () {
    function BezierCurve(points, closeType, increment, bezierCanvas, vertexList) {
        if (vertexList === void 0) { vertexList = null; }
        // A supplied vertex list is already sampled, including closure. This is
        // used by move() and must not append closing control points a second time.
        this.controlPoints =
            vertexList === null ? _preparePoints(points, closeType) : _copy(points);
        this.closeType = closeType === 'CLOSE' ? 'CLOSE' : 'OPEN';
        this.dimension = bezierCanvas.dimension;
        this.increment = increment;
        this.b = bezierCanvas;
        this.vertexList = vertexList === null ? [] : vertexList.slice();
        this.sampledPoints = [];
        this.sampledIncrement = increment;
        if (vertexList === null)
            this._buildVertexList();
        else
            _copy(this.controlPoints, this.sampledPoints);
    }
    BezierCurve.prototype._buildVertexList = function () {
        _sampleBezier(this.controlPoints, this.dimension, this.increment, this.vertexList);
        _copy(this.controlPoints, this.sampledPoints);
        this.sampledIncrement = this.increment;
    };
    BezierCurve.prototype.draw = function (dash) {
        if (dash)
            this._dashedCurve(dash);
        else
            _drawVertices(this.b, this.vertexList, this.closeType);
    };
    BezierCurve.prototype._dashedCurve = function (dash) {
        var _a, _b;
        var solidPart = Math.abs(dash[0]);
        var gapPart = Math.abs(dash[1]);
        if (!Number.isFinite(solidPart) || !Number.isFinite(gapPart)) {
            throw new Error('[p5.bezier] Dash lengths must be finite');
        }
        if (this.increment > 0.001) {
            this.increment = 0.001;
            this._buildVertexList();
            console.warn('[p5.bezier] Smoothness set to 3 for a dashed curve');
        }
        if (solidPart === 0 && gapPart > 0)
            return;
        this.b.beginDash();
        try {
            if (gapPart === 0) {
                _drawVertices(this.b, this.vertexList, this.closeType);
                return;
            }
            var solid = true;
            var needed = solidPart;
            var first = this.vertexList[0];
            this.b.beginPath();
            this.b.moveTo(first[0], first[1], first[2]);
            for (var i = 1; i < this.vertexList.length; i++) {
                var previous = this.vertexList[i - 1];
                var next = this.vertexList[i];
                var x = previous[0];
                var y = previous[1];
                var z = (_a = previous[2]) !== null && _a !== void 0 ? _a : 0;
                var nx = next[0];
                var ny = next[1];
                var nz = (_b = next[2]) !== null && _b !== void 0 ? _b : 0;
                var dx = nx - x;
                var dy = ny - y;
                var dz = nz - z;
                var available = Math.sqrt(dx * dx + dy * dy + dz * dz);
                while (available >= needed) {
                    var ratio = needed / available;
                    x += (nx - x) * ratio;
                    y += (ny - y) * ratio;
                    z += (nz - z) * ratio;
                    if (solid)
                        this.b.lineTo(x, y, z);
                    else
                        this.b.moveTo(x, y, z);
                    available -= needed;
                    solid = !solid;
                    needed = solid ? solidPart : gapPart;
                }
                if (solid)
                    this.b.lineTo(nx, ny, nz);
                needed -= available;
            }
            this.b.closePath('OPEN');
        }
        finally {
            this.b.endDash();
        }
    };
    BezierCurve.prototype.update = function (newControlPointList) {
        if (newControlPointList.length !== this.controlPoints.length) {
            throw new Error('[p5.bezier] The number of control points changed');
        }
        if (this.increment === this.sampledIncrement &&
            _samePoints(this.sampledPoints, newControlPointList)) {
            if (!_samePoints(this.controlPoints, newControlPointList)) {
                this.controlPoints = _copy(newControlPointList);
            }
            return;
        }
        this.controlPoints = _copy(newControlPointList);
        this._buildVertexList();
    };
    BezierCurve.prototype.move = function (x, y, z, toDraw, dash) {
        var _this = this;
        if (z === void 0) { z = null; }
        if (toDraw === void 0) { toDraw = true; }
        if (z === null && this.dimension === 3) {
            throw new Error('[p5.bezier] X, Y, and Z are needed to move a 3D curve');
        }
        var translate = function (points) {
            var result = new Array(points.length);
            for (var i = 0; i < points.length; i++) {
                var point = points[i];
                result[i] =
                    _this.dimension === 3
                        ? [point[0] + x, point[1] + y, point[2] + z]
                        : [point[0] + x, point[1] + y];
            }
            return result;
        };
        var moved = new BezierCurve(translate(this.controlPoints), this.closeType, this.increment, this.b, translate(this.vertexList));
        if (toDraw)
            moved.draw(dash);
        return moved;
    };
    BezierCurve.prototype.shortest = function (pX, pY, pZ) {
        if (pZ === void 0) { pZ = 0; }
        var closest = this.vertexList[0];
        var minimum = Number.POSITIVE_INFINITY;
        for (var i = 0; i < this.vertexList.length; i++) {
            var vertex = this.vertexList[i];
            var dx = vertex[0] - pX;
            var dy = vertex[1] - pY;
            var dz = this.dimension === 3 ? vertex[2] - pZ : 0;
            var distance = dx * dx + dy * dy + dz * dz;
            if (distance < minimum) {
                minimum = distance;
                closest = vertex;
            }
        }
        return closest.slice();
    };
    return BezierCurve;
}());
export default initBezier;
